import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, UserPlus, HandHeart } from "lucide-react";
import { api, memberCardUrl } from "@/lib/api";
import { IMAGES } from "@/content";
import { Download } from "lucide-react";

export default function JoinForm() {
  const [role, setRole] = useState("volunteer");
  const [form, setForm] = useState({ name: "", phone: "", city: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2) return toast.error("કૃપા કરી તમારું નામ લખો");
    if (form.phone.replace(/\D/g, "").length < 10)
      return toast.error("સાચો મોબાઈલ નંબર લખો");
    setLoading(true);
    try {
      const res = await api.joinMember({
        name: form.name.trim(),
        phone: form.phone.trim(),
        city: form.city.trim() || null,
        message: form.message.trim() || null,
        role,
      });
      setDone(res.id);
      toast.success("સ્વાગત છે! તમારી નોંધણી થઈ ગઈ — અમે ટૂંક સમયમાં સંપર્ક કરીશું.");
    } catch {
      toast.error("કંઈક ખોટું થયું — ફરી પ્રયત્ન કરો");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="join" data-testid="join-section" className="py-20 lg:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-5"
        >
          <div className="overflow-hidden rounded-3xl shadow-[0_24px_60px_rgb(31,41,55,0.14)] mb-8">
            <img
              src="/photos/group.jpeg"
              alt="ટ્રસ્ટ પરિવારના સભ્યો અને સ્વયંસેવકો"
              data-testid="join-image"
              className="w-full h-64 sm:h-80 object-cover"
            />
          </div>
          <h2 className="font-display text-ink text-4xl sm:text-5xl leading-snug">
            પરિવારમાં <span className="text-terracotta">જોડાઓ</span>
          </h2>
          <p className="mt-5 text-ink/70 text-base md:text-lg leading-relaxed">
            સભ્ય બનો કે સ્વયંસેવક તરીકે સમય આપો — દરેક યોગદાનથી કોઈ દિવ્યાંગ
            બંધુનું જીવન બદલાય છે.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.12 }}
          className="lg:col-span-7"
        >
          {done ? (
            <div
              data-testid="join-success-message"
              className="rounded-3xl bg-leaf-soft border border-leaf/30 p-10 text-center"
            >
              <HandHeart className="w-14 h-14 text-leaf mx-auto mb-4" />
              <h3 className="font-display text-2xl text-leaf-dark mb-2">
                આભાર, {form.name}!
              </h3>
              <p className="font-head text-ink/70 mb-6">
                તમારી નોંધણી સફળતાપૂર્વક થઈ ગઈ છે. અમારી ટીમ ટૂંક સમયમાં તમારો
                સંપર્ક કરશે.
              </p>
              {done && (
                <a
                  data-testid="join-card-download"
                  href={memberCardUrl(done)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2.5 rounded-full bg-leaf px-7 py-3.5 font-head font-bold text-cream shadow-[0_14px_40px_rgb(30,132,73,0.35)] hover:bg-leaf-dark hover:-translate-y-0.5 transition-all duration-200"
                >
                  <Download className="w-5 h-5" />
                  મેમ્બર કાર્ડ ડાઉનલોડ કરો (PDF)
                </a>
              )}
            </div>
          ) : (
            <form
              data-testid="join-form"
              onSubmit={submit}
              className="rounded-3xl border border-stone-200 bg-cream p-6 sm:p-9 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
            >
              <div className="flex gap-2 rounded-full bg-stone-100 p-1.5 mb-7 w-fit">
                {[
                  { id: "volunteer", label: "સ્વયંસેવક" },
                  { id: "member", label: "સભ્ય" },
                ].map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    data-testid={`join-role-${r.id}`}
                    onClick={() => setRole(r.id)}
                    className={`rounded-full px-5 py-2.5 font-head font-bold text-sm transition-all duration-200 ${
                      role === r.id
                        ? "bg-leaf text-cream shadow-md"
                        : "text-ink/60 hover:text-ink"
                    }`}
                  >
                    {r.label}
                  </button>
                ))}
              </div>

              <div className="grid sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="font-head font-semibold text-sm text-ink/70 block mb-1.5">
                    પૂરું નામ *
                  </label>
                  <input
                    data-testid="join-name-input"
                    value={form.name}
                    onChange={set("name")}
                    placeholder="તમારું નામ"
                    className="w-full rounded-xl border border-stone-300 bg-white px-4 py-3 font-body text-ink outline-none focus:border-leaf focus:ring-2 focus:ring-leaf/20 transition-colors"
                  />
                </div>
                <div>
                  <label className="font-head font-semibold text-sm text-ink/70 block mb-1.5">
                    મોબાઈલ નંબર *
                  </label>
                  <input
                    data-testid="join-phone-input"
                    value={form.phone}
                    onChange={(e) =>
                      setForm({ ...form, phone: e.target.value.replace(/[^0-9+]/g, "") })
                    }
                    placeholder="98XXX XXXXX"
                    className="w-full rounded-xl border border-stone-300 bg-white px-4 py-3 font-body text-ink outline-none focus:border-leaf focus:ring-2 focus:ring-leaf/20 transition-colors"
                  />
                </div>
              </div>
              <div className="mb-4">
                <label className="font-head font-semibold text-sm text-ink/70 block mb-1.5">
                  શહેર / ગામ
                </label>
                <input
                  data-testid="join-city-input"
                  value={form.city}
                  onChange={set("city")}
                  placeholder="દા.તા. અમદાવાદ"
                  className="w-full rounded-xl border border-stone-300 bg-white px-4 py-3 font-body text-ink outline-none focus:border-leaf focus:ring-2 focus:ring-leaf/20 transition-colors"
                />
              </div>
              <div className="mb-7">
                <label className="font-head font-semibold text-sm text-ink/70 block mb-1.5">
                  સંદેશો (વૈકલ્પિક)
                </label>
                <textarea
                  data-testid="join-message-input"
                  value={form.message}
                  onChange={set("message")}
                  rows={3}
                  placeholder="તમે કેવી રીતે યોગદાન આપવા માંગો છો?"
                  className="w-full rounded-xl border border-stone-300 bg-white px-4 py-3 font-body text-ink outline-none focus:border-leaf focus:ring-2 focus:ring-leaf/20 transition-colors resize-none"
                />
              </div>
              <button
                data-testid="join-submit-button"
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2.5 rounded-full bg-leaf px-7 py-4 font-head font-bold text-cream text-lg shadow-[0_14px_40px_rgb(30,132,73,0.35)] hover:bg-leaf-dark hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-60 disabled:translate-y-0"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <UserPlus className="w-5 h-5" />
                )}
                નોંધણી કરો
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
